/* TR cart extras, shared by the cart drawer (snippets/cart-drawer.liquid) and the cart page
   (sections/main-cart-items + main-cart-footer). Quantity / remove / checkout stay Dawn's cart.js.

   - Upsell "Add"       (.tr-upsell__form)          → /cart/add.js
   - Add-on checkbox    ([data-tr-addon])           → /cart/add.js or /cart/change.js (qty 0)
   - Coupon apply/remove ([data-tr-coupon-form])     → /cart/update.js { discount: "CODE1,CODE2" }
   After each change, one /cart?sections=… request re-renders the drawer, the cart page parts
   and the header bubble. All listeners are delegated, so re-rendered markup keeps working. */

(() => {
  if (window.TrCart) return;

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  const post = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }).then((response) => response.json());

  const setBusy = (busy) => {
    document.querySelectorAll('.tr-cart').forEach((el) => el.classList.toggle('is-busy', busy));
  };

  const showError = (message) => {
    const target = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (target) target.textContent = message || window.cartStrings?.error || 'Something went wrong. Please try again.';
  };

  // Re-render every cart surface on the page with a single Section Rendering API request
  async function refresh() {
    const drawer = document.querySelector('cart-drawer');
    const pageItems = document.getElementById('main-cart-items');
    const pageFooter = document.getElementById('main-cart-footer');

    const sections = ['cart-icon-bubble'];
    if (drawer) sections.push('cart-drawer');
    if (pageItems) sections.push(pageItems.dataset.id);
    if (pageFooter) sections.push(pageFooter.dataset.id);

    const data = await fetch(`${routes.cart_url}?sections=${sections.join(',')}`).then((r) => r.json());

    if (drawer && data['cart-drawer']) {
      const source = parse(data['cart-drawer']).querySelector('.drawer__inner');
      const target = drawer.querySelector('.drawer__inner');
      if (source && target) target.innerHTML = source.innerHTML;
      drawer.classList.toggle('is-empty', !!source?.querySelector('.drawer__inner-empty'));
    }

    const swap = (sectionId, selector) => {
      if (!sectionId || !data[sectionId]) return;
      const source = parse(data[sectionId]).querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
      return source;
    };
    const itemsSource = swap(pageItems?.dataset.id, '#main-cart-items .js-contents');
    swap(pageFooter?.dataset.id, '#main-cart-footer .js-contents');
    if (itemsSource) {
      const empty = !itemsSource.querySelector('.cart-item');
      document.querySelector('cart-items')?.classList.toggle('is-empty', empty);
      pageFooter?.classList.toggle('is-empty', empty);
    }

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && data['cart-icon-bubble']) {
      const source = parse(data['cart-icon-bubble']).querySelector('.shopify-section');
      if (source) bubble.innerHTML = source.innerHTML;
    }

    // Let other listeners (e.g. product page quantity rules) know; Dawn's CartItems ignores this source
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
    }
  }

  async function run(task) {
    setBusy(true);
    try {
      await task();
      await refresh();
    } catch (error) {
      showError(error?.message);
    } finally {
      setBusy(false);
    }
  }

  // ---------- Upsell "Add" ----------
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.tr-upsell__form');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button');
    button?.classList.add('is-loading');
    const id = Number(form.querySelector('[name="id"]').value);
    run(async () => {
      const response = await post(`${routes.cart_add_url}.js`, { items: [{ id, quantity: 1 }] });
      if (response.status) throw new Error(response.description || response.message);
    });
  });

  // ---------- Add-on checkbox ----------
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-tr-addon]');
    if (!input) return;
    const { variantId, lineKey } = input.dataset;
    run(async () => {
      const response = input.checked
        ? await post(`${routes.cart_add_url}.js`, { items: [{ id: Number(variantId), quantity: 1 }] })
        : await post(`${routes.cart_change_url}.js`, { id: lineKey, quantity: 0 });
      if (response.status) throw new Error(response.description || response.message);
    });
  });

  // ---------- Coupon ----------
  const currentCodes = (form) => (form?.dataset.codes || '').split(',').map((c) => c.trim()).filter(Boolean);

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-tr-coupon-form]');
    if (!form) return;
    event.preventDefault();
    const input = form.querySelector('input[name="code"]');
    const message = form.querySelector('[data-tr-coupon-message]');
    const code = input.value.trim().toUpperCase();
    const codes = currentCodes(form);

    if (!code) {
      message.textContent = 'Please enter a coupon code.';
      return;
    }
    if (codes.map((c) => c.toUpperCase()).includes(code)) {
      message.textContent = 'This code is already applied.';
      return;
    }
    let result = null;
    run(async () => {
      const response = await post(`${routes.cart_update_url}.js`, { discount: [...codes, code].join(',') });
      if (response.status) throw new Error(response.description || response.message);
      result = couponResult(response, code);
    }).then(() => {
      // The coupon markup was just re-rendered: write the feedback into the fresh message element(s)
      if (result === null) return;
      document.querySelectorAll('[data-tr-coupon-message]').forEach((el) => {
        el.textContent = result ? `Code ${code} applied!` : `Code ${code} is invalid or not applicable to this cart.`;
        el.classList.toggle('is-success', result);
      });
    });
  });

  // true = applied, false = not applicable, null = unknown (older cart JSON without discount_codes)
  function couponResult(cart, code) {
    const match = (value) => String(value || '').toUpperCase() === code;
    if (Array.isArray(cart.discount_codes)) {
      const found = cart.discount_codes.find((d) => match(d.code));
      return Boolean(found?.applicable);
    }
    const applied = (cart.cart_level_discount_applications || []).some((d) => match(d.title)) ||
      (cart.items || []).some((item) => (item.line_level_discount_allocations || []).some((d) => match(d.discount_application?.title)));
    return applied ? true : null;
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-tr-coupon-remove]');
    if (!button) return;
    const form = button.closest('.tr-coupon')?.querySelector('[data-tr-coupon-form]');
    const remove = button.dataset.trCouponRemove.toUpperCase();
    const remaining = currentCodes(form).filter((c) => c.toUpperCase() !== remove);
    run(async () => {
      const response = await post(`${routes.cart_update_url}.js`, { discount: remaining.join(',') });
      if (response.status) throw new Error(response.description || response.message);
    });
  });

  window.TrCart = { refresh };
})();
